'use client';

import { useContext, useState, type FC } from 'react';
import {
  Button,
  ButtonGroup,
  Text,
  Dialog,
  Flex,
  Separator,
} from '@/_components';
import { saveQuestionnaire } from '@/_api/client-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';

import type { ExportQuestionnaireConfirmationDialogProps } from './ExportQuestionnaireConfirmationDialog.types';

export const ExportQuestionnaireConfirmationDialog: FC<
  ExportQuestionnaireConfirmationDialogProps
> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { questionnaireExport, loadQuestionnaires } =
    useContext(QuestionnaireContext);

  const updateQuestionnaire = async (params: any, toastMessage: string) => {
    setIsLoading(true);

    try {
      await saveQuestionnaire({
        companyId: questionnaireExport[0]?.company_id!,
        id: questionnaireExport?.[0].id!,
        params,
        token,
      });

      loadQuestionnaires(undefined, true);

      addToast('success', toastMessage);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onTrackChanges = async () => {
    await updateQuestionnaire({ track_changes: true }, 'Now tracking changes');
  };

  const onLockQuestionnaire = async () => {
    await updateQuestionnaire({ locked: true }, 'Questionnaire locked');
  };

  return (
    <Dialog isNarrow={true} isOpen={isOpen} onClose={onClose}>
      <Dialog.Content>
        <Flex gap={8} isColumn={true}>
          <Flex isColumn={true} gap={4} padding={4} isCentered={true}>
            <Text color="accent" fontWeight="bold">
              Export in progress
            </Text>
            <Text fontWeight="bold">
              You will get an in-app notification on your top right to download
              the export.
            </Text>
          </Flex>

          <Separator isLight={true} />

          <Flex isColumn={true} gap={6} isCentered={true}>
            <Text type="small" fontWeight="bold">
              Suggested Actions
            </Text>
            <ButtonGroup isSameLine={true} isFullWidth={true}>
              <Button
                intent="secondary"
                type="button"
                value="Lock Questionnaire"
                isDisabled={isLoading}
                onClick={onLockQuestionnaire}
              />
              <Button
                intent="secondary"
                type="button"
                value="Track Changes"
                isDisabled={isLoading}
                onClick={onTrackChanges}
              />
            </ButtonGroup>
            <ButtonGroup isFullWidth={true}>
              <Button intent="primary" value="Close" onClick={onClose} />
            </ButtonGroup>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
